import React, { useCallback, useEffect, useMemo, useState } from 'react'
import { makeStyles } from '@material-ui/core'
import { useForm } from 'react-hook-form'
import PropTypes from 'prop-types'
import SectionHeader from '../../shared/SectionHeader'
import EditButton from '../../shared/EditButton'
import Text from '../../../../atoms/Text'
import SubmitButtons from '../SubmitButtons'
import { useAssignAccountClients } from '../../../../../api/groups'
import AccountClientsTable from './AccountClientsTable'
import { AccountClientsTableContext } from './AccountClientsTableContext'

const useStyles = makeStyles(() => ({
  titleAdornment: {
    display: 'flex',
    color: '#9296A5',
    flexDirection: 'row',
    '& > span': {
      color: '#5A5E6D',
      marginLeft: '0.25rem'
    }
  }
}))

const AccountClientAssignments = ({ clientsAssigned, clientsUnAssigned, id, onSearchClients, isLoading, onSubmit }) => {
  const [editing, setEditing] = useState(false)
  const [saving, setSaving] = useState(false)
  const clients = [...clientsAssigned.data]
  const classes = useStyles()
  const [clientEditList, setClientEditList] = useState([])
  const [filteredList, setFilteredList] = useState([])
  const [searchStr, setSearchStr] = useState('')
  const { mutateAsync: saveAccountClients } = useAssignAccountClients()
  useEffect(() => {
    const clientEditListSource = [
      ...clientsAssigned.data.map(item => ({
        value: item,
        assigned: true,
        edited: false
      })),
      ...clientsUnAssigned.data.map(item => ({
        value: item,
        assigned: false,
        edited: false
      }))
    ].filter(item => item.value.name.toLowerCase().includes(searchStr.toLowerCase()))
    setClientEditList(clientEditListSource)
    setFilteredList(clientEditListSource)
  }, [searchStr, clientsAssigned, clientsUnAssigned])
  const updateAssignment = useCallback((clientId) => {
    setFilteredList(prevList =>
      prevList.map(item =>
        item.value.id === clientId
          ? { ...item, edited: !item.edited }
          : item
      )
    )
    setClientEditList(prevList =>
      prevList.map(item =>
        item.value.id === clientId
          ? { ...item, edited: !item.edited }
          : item
      )
    )
  }, [])
  const onClickClient = useCallback(
    (id) => {
      if (editing) return
      const url = `${window.location.origin}/admin/clients/${id}`
      window.open(url, '_blank')
    },
    [editing]
  )
  const resetEdits = useCallback(
    () => {
      setEditing(false)
      setFilteredList(prevList =>
        prevList.map(item =>
          item.edited ? { ...item, edited: false } : item
        ))
      setClientEditList(prevList =>
        prevList.map(item =>
          item.edited ? { ...item, edited: false } : item
        ))
    }, []
  )
  const searchEditList = useCallback(
    (searchStr) => {
      onSearchClients(searchStr)
      setSearchStr(searchStr)
    }, [onSearchClients]
  )

  useEffect(() => {
    if (editing) {
      setFilteredList(clientEditList)
    } else {
      setFilteredList(clientEditList.filter(item => item.assigned))
    }
  }, [clientEditList, editing])

  const {
    handleSubmit
  } = useForm({
    defaultValues: editing ? filteredList : clientEditList
  })

  const submitAccountClients = async () => {
    setSaving(true)
    const result = await saveAccountClients({
      accountId: parseInt(id),
      clients: clientEditList.filter(item => item.edited).map(item => ({
        clientId: item.value.id,
        assigned: item.assigned,
        edited: item.edited
      }))
    })
    if (result && result.success && result.list.length > 0) {
      const newClientList = clientEditList.map(item => {
        if (item.edited) {
          item.edited = false
          item.assigned = !item.assigned
        }
        return item
      })
      setClientEditList(newClientList)
      setEditing(false)
      onSubmit?.()
    }
    setSaving(false)
    return result
  }

  const providerValue = useMemo(() => {
    return { clientsAssigned, clientsUnAssigned, clientEditList, setClientEditList, updateAssignment, onClickClient, resetEdits, searchEditList, filteredList, saving }
  }, [clientsAssigned, clientsUnAssigned, clientEditList, updateAssignment, onClickClient, resetEdits, searchEditList, filteredList, saving])
  return (
    <div>
      <AccountClientsTableContext.Provider value={providerValue}>
        <form onSubmit={handleSubmit(submitAccountClients)}>
          <SectionHeader text='Assigned Households'>
            <EditButton
              policy='admin_edit_acct_client'
              onClick={() => setEditing(!editing)}
            />
            <div className={classes.titleAdornment}>
              <Text text={clientEditList.filter(item => item.assigned).length || '0'} />
              <span>
                <Text text='Total' />
              </span>
            </div>
          </SectionHeader>
          <AccountClientsTable clients={clients} editing={editing} isLoading={isLoading} />
          <SubmitButtons editMode={editing} onCancel={() => resetEdits()} />
        </form>
      </AccountClientsTableContext.Provider>
    </div>
  )
}

AccountClientAssignments.propTypes = {
  clientsAssigned: PropTypes.object,
  clientsUnAssigned: PropTypes.object,
  id: PropTypes.string,
  onSearchClients: PropTypes.func,
  isLoading: PropTypes.bool,
  onSubmit: PropTypes.func
}

export default AccountClientAssignments
